import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchIssueStatus, fetchVerifyStatus, startIssuance, startVerification } from './PlaygroundApi';

// 'pending', 'success' and 'fail' are the labels the backend returns
// (IssuanceStatusResponse.Status / VerifyStatusResponse.Status).
export type FlowStatus = 'idle' | 'starting' | 'pending' | 'success' | 'fail';

export interface FlowState {
    selectedKey: string | undefined;
    sessionId: string | undefined;
    requestUri: string | undefined;
    status: FlowStatus;
    // What the visitor reads, as a translation key. `error` holds the raw text from
    // axios or from the backend's `reason`; that is useful on a developer playground
    // but is never translated, so it is shown as detail under the translated headline
    // rather than as the message itself.
    errorKey: string | undefined;
    error: string | undefined;
}

export interface VerifyFlowState extends FlowState {
    attributes: Record<string, unknown> | undefined;
}

export interface PlaygroundState {
    issue: FlowState;
    verify: VerifyFlowState;
    // credentialType values issued during this visit. The page cannot read the
    // wallet, so this reflects what happened here, not what the wallet holds.
    // Losing it on refresh is acceptable: re-issuing an existing credential is
    // harmless, and a wrong "you already have it" would be worse.
    issuedCredentials: string[];
}

const idleFlow: FlowState = {
    selectedKey: undefined,
    sessionId: undefined,
    requestUri: undefined,
    status: 'idle',
    errorKey: undefined,
    error: undefined,
};

const initialState: PlaygroundState = {
    issue: idleFlow,
    verify: {...idleFlow, attributes: undefined},
    issuedCredentials: [],
};

export const playgroundSlice = createSlice({
    name: 'playground',
    initialState,
    reducers: {
        resetIssue: (state) => {
            state.issue = idleFlow;
        },
        resetVerify: (state) => {
            state.verify = {...idleFlow, attributes: undefined};
        },
        selectIssueCredential: (state, action: PayloadAction<{selectedKey: string, sessionId: string}>) => {
            state.issue = {...idleFlow, ...action.payload, status: 'starting'};
        },
        selectVerifyProof: (state, action: PayloadAction<{selectedKey: string, sessionId: string}>) => {
            state.verify = {...idleFlow, ...action.payload, status: 'starting', attributes: undefined};
        },
        issueTimedOut: (state) => {
            state.issue.status = 'fail';
            state.issue.errorKey = 'qr.timedOut';
            state.issue.error = undefined;
        },
        verifyTimedOut: (state) => {
            state.verify.status = 'fail';
            state.verify.errorKey = 'qr.timedOut';
            state.verify.error = undefined;
        },
    },
    extraReducers: builder => {
        builder.addCase(startIssuance.fulfilled, (state, action: PayloadAction<any>) => {
            state.issue.sessionId = action.payload.sessionId;
            state.issue.requestUri = action.payload.request_uri;
            state.issue.status = 'pending';
        });
        builder.addCase(startIssuance.rejected, (state, action) => {
            state.issue.status = 'fail';
            state.issue.errorKey = 'qr.failed';
            state.issue.error = action.error.message;
        });
        builder.addCase(fetchIssueStatus.fulfilled, (state, action: PayloadAction<any>) => {
            state.issue.status = action.payload.status;
            const failed = action.payload.status === 'fail';
            state.issue.errorKey = failed ? 'qr.failed' : undefined;
            state.issue.error = failed ? action.payload.reason : undefined;
            const issued = state.issue.selectedKey;
            if (action.payload.status === 'success' && issued && !state.issuedCredentials.includes(issued)) {
                state.issuedCredentials.push(issued);
            }
        });
        builder.addCase(fetchIssueStatus.rejected, (state, action) => {
            state.issue.status = 'fail';
            state.issue.errorKey = 'qr.failed';
            state.issue.error = action.error.message;
        });

        builder.addCase(startVerification.fulfilled, (state, action: PayloadAction<any>) => {
            state.verify.sessionId = action.payload.sessionId;
            state.verify.requestUri = action.payload.request_uri;
            state.verify.status = 'pending';
        });
        builder.addCase(startVerification.rejected, (state, action) => {
            state.verify.status = 'fail';
            state.verify.errorKey = 'qr.failed';
            state.verify.error = action.error.message;
        });
        builder.addCase(fetchVerifyStatus.fulfilled, (state, action: PayloadAction<any>) => {
            state.verify.status = action.payload.status;
            state.verify.attributes = action.payload.attributes;
            const failed = action.payload.status === 'fail';
            state.verify.errorKey = failed ? 'qr.failed' : undefined;
            state.verify.error = failed ? action.payload.reason : undefined;
        });
        builder.addCase(fetchVerifyStatus.rejected, (state, action) => {
            state.verify.status = 'fail';
            state.verify.errorKey = 'qr.failed';
            state.verify.error = action.error.message;
        });
    },
});

export const {
    resetIssue,
    resetVerify,
    selectIssueCredential,
    selectVerifyProof,
    issueTimedOut,
    verifyTimedOut,
} = playgroundSlice.actions;
